import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Plus, Trash2, Loader2, RefreshCw, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RecipientRoleSelect from '../components/RecipientRoleSelect';

const ReviewAndSend = () => {
  const { envelopeId } = useParams();
  const { API, accessToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [envelope, setEnvelope] = useState(null);
  const [recipients, setRecipients] = useState([
    { name: '', email: '', role: 'Signer', routing_order: 1 }
  ]);
  const [fields, setFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const previewImageRef = useRef(null);

  useEffect(() => {
    fetchEnvelopeDetails();
  }, [envelopeId]);
  
  // Cleanup preview image URL on unmount
  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [previewImageUrl]);

  const fetchEnvelopeDetails = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      
      // Fetch envelope
      const envResponse = await axios.get(`${API}/envelopes/${envelopeId}`, { headers });
      setEnvelope(envResponse.data);
      
      // Fetch recipients
      const recipientsResponse = await axios.get(`${API}/envelopes/${envelopeId}/recipients`, { headers });
      const existingRecipients = recipientsResponse.data || [];

      if (existingRecipients.length > 0) {
        setRecipients(existingRecipients.map(r => ({
          id: r.id,
          name: r.display_name || r.name,
          email: r.email,
          role: r.role || 'Signer',
          routing_order: r.routing_order || 1
        })));
      } else {
        // No recipients → "Fields only" draft; redirect to field editor directly
        navigate('/send/editor', {
          state: { envelopeId, documentTitle: envResponse.data?.title }
        });
        return;
      }
      
      // Fetch fields
      const fieldsResponse = await axios.get(`${API}/envelopes/${envelopeId}/fields`, { headers });
      setFields(fieldsResponse.data || []);
      
      // Load preview image
      await loadPreviewImage();
      
    } catch (error) {
      console.error('Failed to load envelope details:', error);
      toast.error('Failed to load envelope details');
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadPreviewImage = async () => {
    try {
      setLoadingPreview(true);
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/envelopes/${envelopeId}/preview-image?t=${timestamp}`, {
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        responseType: 'blob'
      });
      
      const imageUrl = URL.createObjectURL(response.data);
      setPreviewImageUrl(imageUrl);
    } catch (error) {
      console.error('Failed to load preview image:', error);
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const addRecipient = () => {
    setRecipients([...recipients, { 
      name: '', 
      email: '', 
      role: 'Signer', 
      routing_order: recipients.length + 1 
    }]);
  };

  const removeRecipient = (index) => {
    if (recipients.length === 1) return;
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const handleSend = async () => {
    // Validate recipients
    const validRecipients = recipients.filter(r => r.name.trim() && r.email.trim());
    
    if (validRecipients.length === 0) {
      toast.error('Please add at least one recipient with name and email');
      return;
    }

    try {
      setIsSending(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      
      // Save recipients first
      await axios.post(
        `${API}/envelopes/${envelopeId}/recipients`,
        { recipients: validRecipients.map((r, idx) => ({
          name: r.name,
          email: r.email,
          role: r.role,
          signing_order: idx + 1
        })) },
        { headers }
      );
      
      // Send envelope
      await axios.post(
        `${API}/envelopes/${envelopeId}/send`,
        { message: emailMessage },
        { headers }
      );
      
      toast.success('Document sent successfully!');
      navigate('/envelopes?tab=sent');
      
    } catch (error) {
      console.error('Send failed:', error);
      toast.error('Failed to send document');
    } finally {
      setIsSending(false);
    }
  };

  const handleBackToEditor = () => {
    navigate('/send/editor', {
      state: {
        envelopeId,
        documentTitle: envelope?.title
      }
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading envelope...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{envelope?.title}</h1>
              <p className="text-sm text-gray-500 mt-1">Prepare your document for signing</p>
            </div>
            <button
              onClick={handleBackToEditor}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ← Back to Editor
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Document Preview */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Document Preview</h2>
              <button
                onClick={loadPreviewImage}
                disabled={loadingPreview}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md disabled:opacity-50"
              >
                <RefreshCw size={16} className={loadingPreview ? 'animate-spin' : ''} />
                Refresh Preview
              </button>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 overflow-auto" 
              style={{ maxHeight: '70vh' }}
            >
              {loadingPreview ? (
                <div className="flex items-center justify-center h-[500px]">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Generating preview...</p>
                  </div>
                </div>
              ) : previewImageUrl ? (
                <img 
                  ref={previewImageRef}
                  src={previewImageUrl} 
                  alt="Document preview with field overlays"
                  className="w-full h-auto"
                  style={{ display: 'block' }}
                />
              ) : (
                <div className="flex items-center justify-center h-[500px]">
                  <p className="text-gray-500">Preview not available</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Recipients & Settings */}
          <div className="space-y-6">
            {/* Recipients Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recipients</h2>
                <button
                  onClick={addRecipient}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md"
                >
                  <Plus size={16} />
                  Add Recipient
                </button>
              </div>

              <div className="space-y-4">
                {recipients.map((recipient, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        Recipient {index + 1}
                      </span>
                      {recipients.length > 1 && (
                        <button
                          onClick={() => removeRecipient(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={recipient.name}
                          onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                          placeholder="Enter recipient name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={recipient.email}
                          onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                          placeholder="recipient@example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <RecipientRoleSelect
                          value={recipient.role}
                          onChange={(newRole) => updateRecipient(index, 'role', newRole)}
                          isTemplateFlow={false}
                          recipients={recipients}
                          currentIndex={index}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Signing Order
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={recipient.routing_order}
                          onChange={(e) => updateRecipient(index, 'routing_order', parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Message */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                ✉️ Email Message (Optional)
              </h3>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a personal message to include in the email notification..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"
              />
            </div>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={isSending}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReviewAndSend;
